import { keepPreviousData, skipToken, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import { isEmptyString } from '@/utils/string';
import { ATTRIBUTES_PAGE_SIZE, attributeKeys, type AttributeListScope } from './entity';

type AttributesListQuery = NonNullable<
  NonNullable<NonNullable<Parameters<typeof hydraUiApi.attributes.list>[0]>['params']>['query']
>;

function buildListQuery(page: number, scope: AttributeListScope): AttributesListQuery {
  const query: AttributesListQuery = { page, size: ATTRIBUTES_PAGE_SIZE };
  if (scope.search != null) query.search = scope.search;
  if (scope.inUseOnly === true) query.inUseOnly = true;
  if (scope.objectTypeId != null) query.objectType = Number(scope.objectTypeId);
  if (scope.relationship === true) query.relationship = true;
  return query;
}

/** @public */
export async function getAttributeNamesPage(
  workspaceId: number,
  { page, ...scope }: AttributeListScope & { page: number },
): Promise<string[]> {
  const raw = await hydraUiApi.attributes.list({
    params: {
      path: { environment_id: String(workspaceId) },
      query: buildListQuery(page, scope),
    },
  });
  return raw.attributes.map((attribute) => attribute.id);
}

/** One page of attribute names with the size of the whole list they come from. */
export type AttributeNamesPage = { names: string[]; total: number };

/** @public */
export async function getAttributeNamesFirstPage(
  workspaceId: number,
  scope: AttributeListScope,
  signal?: AbortSignal,
): Promise<AttributeNamesPage> {
  const path = { environment_id: String(workspaceId) };
  const { page: _page, size: _size, ...totalsQuery } = buildListQuery(1, scope);
  // The list never fills in its pagination total (it is too slow on large
  // workspaces); the totals endpoint counts the same scope instead.
  const [raw, totals] = await Promise.all([
    hydraUiApi.attributes.list({ params: { path, query: buildListQuery(1, scope) }, signal }),
    hydraUiApi.attributes.getAttributesTotals({ params: { path, query: totalsQuery }, signal }),
  ]);
  const names = raw.attributes.map((attribute) => attribute.id);
  const counted = scope.inUseOnly === true ? (totals.totals.in_use ?? totals.totals.all) : totals.totals.all;
  return { names, total: Math.max(counted, names.length) };
}

/**
 * The first page of attribute names and how many match in all, for a picker
 * that shows one page and asks for a narrower search rather than paging.
 * A changed scope keeps the previous page on screen until the next arrives.
 *
 * @public
 */
export function useAttributeNamesFirstPage(workspaceId: number, scope: AttributeListScope = {}) {
  return useQuery({
    queryKey: attributeKeys.firstPage(workspaceId, scope),
    queryFn: ({ signal }) => getAttributeNamesFirstPage(workspaceId, scope, signal),
    staleTime: 0,
    retry: false,
    placeholderData: keepPreviousData,
  });
}

type AttributeCatalogOptions = {
  /** Whether the catalog is read at all; off, the hook holds no data and fetches nothing. */
  isEnabled?: boolean;
};

/**
 * The server-paged attribute-name catalog. The first page loads on mount;
 * callers pull further pages on demand with `fetchNextPage` (a short page
 * ends the catalog). A failed page load keeps `hasNextPage` true so the next
 * pull retries. Never served from cache (`staleTime: 0`) — attributes are
 * written by ingestion, not this app, so a cached catalog can be stale.
 *
 * A changed scope (search term, in-use filter, object type) is a new catalog
 * starting from page 1; the previous scope's pages stay visible while the
 * first page loads, so a keystroke doesn't blank a picker.
 *
 * @public
 */
export function useAttributeCatalog(
  workspaceId: number,
  scope: AttributeListScope = {},
  { isEnabled = true }: AttributeCatalogOptions = {},
) {
  return useInfiniteQuery({
    enabled: isEnabled,
    queryKey: attributeKeys.catalog(workspaceId, scope),
    queryFn: ({ pageParam }) => getAttributeNamesPage(workspaceId, { page: pageParam, ...scope }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.length < ATTRIBUTES_PAGE_SIZE ? undefined : lastPageParam + 1,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
}

/** @public */
export async function getAttributeValues(workspaceId: number, attribute: string): Promise<string[]> {
  const raw = await hydraUiApi.valueLists.getAttributesValues({
    params: { path: { environment_id: String(workspaceId) }, query: { attribute } },
  });
  return raw.values;
}

/**
 * Autosuggest values for one profile attribute. Only fires while an attribute
 * is named; a failed load yields no suggestions rather than an error state —
 * the value input accepts free text either way.
 *
 * @public
 */
export function useAttributeValues(workspaceId: number, attribute: string) {
  const active = !isEmptyString(attribute);
  return useQuery({
    queryKey: attributeKeys.values(workspaceId, attribute),
    queryFn: active ? () => getAttributeValues(workspaceId, attribute) : skipToken,
    staleTime: 0,
    retry: false,
  });
}

async function getObjectAttributeValues(
  workspaceId: number,
  objectTypeId: string,
  attribute: string,
  isRelationship: boolean,
) {
  const raw = await hydraUiApi.valueLists.getObjectAttributesValues({
    params: {
      path: { environment_id: String(workspaceId), type_id: objectTypeId },
      query: isRelationship ? { attribute, relationship: true } : { attribute },
    },
  });
  return raw.values;
}

type ObjectAttributeValuesOptions = {
  /** Whether the attribute is one of the object type's relationship attributes rather than its own. */
  isRelationship?: boolean;
};

/** Autosuggest values for one object attribute. Free text remains available if this request fails. */
export function useObjectAttributeValues(
  workspaceId: number,
  objectTypeId: string,
  attribute: string,
  { isRelationship = false }: ObjectAttributeValuesOptions = {},
) {
  const active = !isEmptyString(attribute);
  return useQuery({
    queryKey: attributeKeys.objectValues(workspaceId, objectTypeId, attribute, isRelationship),
    queryFn: active ? () => getObjectAttributeValues(workspaceId, objectTypeId, attribute, isRelationship) : skipToken,
    staleTime: 0,
    retry: false,
  });
}

/**
 * Server-side attribute-name search (first page of matches). Only fires while
 * there is a search term; previous matches stay visible while a narrower term
 * loads, so a keystroke doesn't blank the results.
 *
 * @public
 */
export function useAttributeNameSearch(workspaceId: number, search: string) {
  const active = !isEmptyString(search);
  return useQuery({
    queryKey: active ? attributeKeys.search(workspaceId, search) : attributeKeys.searchInactive(workspaceId),
    queryFn: active ? () => getAttributeNamesPage(workspaceId, { page: 1, search }) : skipToken,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
}
